import { useState } from 'react';
import { BookOpen, Sparkles, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import Image from './ui/Artwork';
import GameLayout from './GameLayout';
import RulesContent from './RulesContent';

interface HomeScreenProps {
  nickname: string;
  roomCode: string;
  loading?: boolean;
  error?: string;
  onNicknameChange: (value: string) => void;
  onRoomCodeChange: (value: string) => void;
  onCreate: () => void;
  onJoin: () => void;
}

const previewCards = [
  { file: '0', name: 'A world of dreams', clue: 'Somewhere only we know' },
  { file: '10', name: 'An unexpected journey', clue: 'The long way home' },
  { file: '15', name: 'A little mystery', clue: 'Nothing is quite as it seems' },
  { file: '20', name: 'A story waiting to happen', clue: 'When the world falls quiet' },
  { file: '2', name: 'Something extraordinary', clue: 'A beautiful impossibility' },
];

export default function HomeScreen({ nickname, roomCode, loading, error, onNicknameChange, onRoomCodeChange, onCreate, onJoin }: HomeScreenProps) {
  const [activeCard, setActiveCard] = useState(2);
  // Keep the form in place when someone types a code during their visit.
  const [arrivedWithInvitation] = useState(() => Boolean(roomCode.trim()));
  const changeCard = (direction: number) => setActiveCard(current => (current + direction + previewCards.length) % previewCards.length);

  return (
    <GameLayout>
      <main className={`ev-home ev-shell${arrivedWithInvitation ? ' ev-home-invited' : ''}`}>
        <header className="ev-masthead"><h1 className="ev-brand" data-testid="lobby-title">Evoke</h1><div className="ev-home-meta"><span>A little mystery. A lot of imagination.</span><span className="ev-meta"><Users size={15} aria-hidden="true" /> 3–8 players</span></div></header>
        <div className="ev-home-layout">
          <section className="ev-home-intro" aria-labelledby="home-heading">
            <h2 id="home-heading" className="ev-display">Every picture.
A thousand possibilities.</h2>
            <p>A storytelling game of beautiful misdirection.
See how well your friends read your mind.</p>
            <div className="ev-art-stage">
              <div className="ev-art-halo" aria-hidden="true" />
              <div className="ev-art-fan" role="group" aria-label="Explore the artwork">
                {previewCards.map((card, index) => {
                  const offset = (index - activeCard + 7) % 5 - 2;
                  return <button type="button" key={card.file} className="ev-art-fan-card" aria-label={`Explore ${card.name}`} aria-pressed={index === activeCard} onClick={() => setActiveCard(index)} style={{ '--card-turn': `${offset * 12}deg`, '--card-lift': `${Math.abs(offset) * 22}px`, '--card-offset': `${offset * 63}%`, zIndex: 5 - Math.abs(offset) } as React.CSSProperties}>
                    <Image src={`/cards/preview/${card.file}.jpg.responsive-v1/768.webp`} alt={card.name} fill className="object-cover" sizes="(min-width: 1024px) 220px, (min-width: 768px) 170px, 120px" priority={index === 2} />
                  </button>;
                })}
              </div>
              <div className="ev-art-caption">
                <button type="button" className="ev-icon-button ev-icon-on-dark" aria-label="Previous artwork" onClick={() => changeCard(-1)}><ChevronLeft size={18} /></button>
                <div><p>One picture. Your own interpretation.</p><p className="ev-example-clue" aria-live="polite" aria-atomic="true">“{previewCards[activeCard].clue}”</p></div>
                <button type="button" className="ev-icon-button ev-icon-on-dark" aria-label="Next artwork" onClick={() => changeCard(1)}><ChevronRight size={18} /></button>
              </div>
            </div>
          </section>
          <section className="ev-home-form ev-paper" aria-label="Create or join a game">
            <div className="ev-invitation-mark" aria-hidden="true"><Sparkles size={23} /></div><div className="ev-form-heading"><h2>{roomCode.trim() ? 'Your seat is waiting.' : 'Pull up a chair.'}</h2><p>Your next good story starts around this table.</p></div>
            <form onSubmit={event => { event.preventDefault(); if (roomCode.trim()) onJoin(); else onCreate(); }}>
              <label className="ev-label" htmlFor="nickname">Your name</label>
              <input id="nickname" className="ev-input" value={nickname} onChange={event => onNicknameChange(event.target.value)} placeholder="What should we call you?" data-testid="lobby-nickname-input" maxLength={20} disabled={loading} autoComplete="nickname" required />
              <div className="ev-home-actions">
              {!roomCode.trim() && <><button type="submit" disabled={!nickname.trim() || loading} data-testid="lobby-create-button" className="ev-button ev-home-create"><Sparkles size={17} aria-hidden="true" />{loading ? 'Creating…' : 'Create a room'}</button><div className="ev-divider"><span>or join your friends</span></div></>}
              <div className="ev-home-join">
                <label className="ev-label" htmlFor="room-code">Have an invitation?</label>
                <div className="ev-join-row"><input id="room-code" className="ev-input ev-code-input" value={roomCode} onChange={event => onRoomCodeChange(event.target.value.toUpperCase())} placeholder="Room code" data-testid="lobby-roomcode-input" maxLength={6} disabled={loading} autoComplete="off" autoCapitalize="characters" spellCheck={false} /><button type="submit" disabled={!nickname.trim() || !roomCode.trim() || loading} data-testid="lobby-join-button" className={`ev-button${roomCode.trim() ? '' : ' ev-button-secondary'}`}>{loading && roomCode ? 'Joining…' : 'Join room'}</button></div>
              </div>
              </div>
              {error && <p role="alert" className="ev-alert mt-3" data-testid="lobby-error">{error}</p>}
            </form>
            <p className="ev-form-footnote"><Users size={14} aria-hidden="true" /> Play together, each on your own screen.</p>
          </section>
        </div>
        <div className="ev-how-heading"><h2 className="ev-display">Good stories bring people together.</h2><p>Here’s how a round unfolds.</p></div>
        <ol className="ev-home-steps" aria-label="A round of Evoke">
          <li><span>1</span><div><h3>Leave a little mystery.</h3><p>Give a clue inspired by your card.</p></div></li>
          <li><span>2</span><div><h3>Play their imagination.</h3><p>Everyone adds a card that fits.</p></div></li>
          <li><span>3</span><div><h3>Find the original.</h3><p>Guess the storyteller’s card.</p></div></li>
        </ol>
        <details className="ev-home-rules ev-disclosure"><summary><BookOpen size={17} aria-hidden="true" /> New to Evoke? <span>How to play</span></summary><div className="ev-paper p-4 sm:p-5"><RulesContent variant="compact" /></div></details>
      </main>
    </GameLayout>
  );
}
